/**
 * reCAPTCHA Enterprise — same script as Google's simple demo:
 * https://www.google.com/recaptcha/enterprise.js (async defer)
 * Widget: enterprise.render(..., { sitekey, action: "LOGIN", ... }) like g-recaptcha + data-action.
 *
 * Site key: set REACT_APP_RECAPTCHA_SITE_KEY to match backend RECAPTCHA_ENTERPRISE_SITE_KEY.
 */

const ENTERPRISE_SCRIPT_MARKER = "recaptcha/enterprise.js";

export const getSiteKey = () => process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

let recaptchaWidgetId = null;
let simpleScriptPromise = null;

function enterpriseReady() {
  return new Promise((resolve) => {
    if (window.grecaptcha?.enterprise?.ready) {
      window.grecaptcha.enterprise.ready(resolve);
    } else {
      resolve();
    }
  });
}

/**
 * Load Enterprise script (no render=explicit query — matches official HTML demo).
 */
export function loadRecaptchaEnterpriseSimple() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.grecaptcha?.enterprise?.render) return enterpriseReady();

  const existing = document.querySelector(
    `script[src*="${ENTERPRISE_SCRIPT_MARKER}"]`
  );
  if (existing) return enterpriseReady();

  if (simpleScriptPromise) return simpleScriptPromise;

  simpleScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/enterprise.js";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      enterpriseReady().then(resolve).catch(reject);
    };
    script.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
    document.head.appendChild(script);
  });

  return simpleScriptPromise;
}

export const loadRecaptcha = loadRecaptchaEnterpriseSimple;

export async function mountEnterpriseCheckbox(containerEl, options = {}) {
  const {
    onVerify,
    onExpire,
    action = "submit",
    theme = "light",
    size = "compact",
  } = options;

  await loadRecaptchaEnterpriseSimple();

  const siteKey = getSiteKey();
  if (!siteKey) {
    console.warn("REACT_APP_RECAPTCHA_SITE_KEY is not set");
    return null;
  }

  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(() => {
      try {
        if (!containerEl) {
          resolve(null);
          return;
        }
        containerEl.innerHTML = "";
        const inner = document.createElement("div");
        containerEl.appendChild(inner);

        recaptchaWidgetId = window.grecaptcha.enterprise.render(inner, {
          sitekey: siteKey,
          action,
          theme,
          size,
          callback: onVerify,
          "expired-callback": onExpire,
        });
        resolve(recaptchaWidgetId);
      } catch (e) {
        reject(e);
      }
    });
  });
}

/**
 * @param {string|HTMLElement} containerId
 * @param {object} [options] callback, expiredCallback, action, theme, size
 * @returns {Promise<number|null>}
 */
export function renderRecaptcha(containerId, options = {}) {
  const el =
    typeof containerId === "string"
      ? document.getElementById(containerId)
      : containerId;
  if (!el) {
    if (typeof containerId === "string") {
      console.error(`reCAPTCHA container #${containerId} not found`);
    }
    return Promise.resolve(null);
  }

  const {
    callback,
    expiredCallback,
    action = "submit",
    theme = "light",
    size = "compact",
  } = options;

  return mountEnterpriseCheckbox(el, {
    onVerify: callback,
    onExpire: expiredCallback,
    action,
    theme,
    size,
  });
}

export function resetRecaptcha() {
  if (!window.grecaptcha?.enterprise) {
    return;
  }
  try {
    if (recaptchaWidgetId !== null) {
      window.grecaptcha.enterprise.reset(recaptchaWidgetId);
    } else {
      window.grecaptcha.enterprise.reset();
    }
  } catch (e) {
    console.error("Error resetting reCAPTCHA:", e);
  }
}

export function getRecaptchaToken() {
  if (!window.grecaptcha?.enterprise) {
    return null;
  }
  try {
    if (recaptchaWidgetId !== null) {
      return (
        window.grecaptcha.enterprise.getResponse(recaptchaWidgetId) || null
      );
    }
    return window.grecaptcha.enterprise.getResponse() || null;
  } catch (e) {
    console.error("Error getting reCAPTCHA token:", e);
    return null;
  }
}

export function isRecaptchaConfigured() {
  return Boolean(getSiteKey());
}

/**
 * Contact forms: user must complete the visible checkbox first; returns its token.
 * @param {string} [_action] e.g. 'contact_form' — reserved for future score-based flows
 */
export const executeRecaptcha = async (_action) => {
  await loadRecaptchaEnterpriseSimple();
  return getRecaptchaToken();
};
